import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'

import { cn } from '@/lib/css-utils'

const commonButtonStyles = {
  base: 'inline-flex items-center justify-center whitespace-nowrap rounded-3xl h-fit font-medium transition-colors \
          focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-button-outline-focus-border \
          disabled:pointer-events-none disabled:opacity-50 transition-colors cursor-pointer',
  variants: {
    default:
      'bg-button-primary-bg text-button-primary-text \
        hover:bg-button-primary-hover-bg \
        active:bg-button-primary-active-bg \
        disabled:bg-button-primary-disabled-bg dark:disabled:border dark:disabled:border-button-primary-disabled-text',
    secondary:
      'bg-button-secondary-bg text-button-secondary-text \
        hover:bg-button-secondary-hover-bg \
        active:bg-button-secondary-active-bg \
        disabled:bg-button-primary-disabled-bg',
    outline:
      'bg-button-outline-bg text-button-outline-text shadow-xs border border-button-outline-border \
        hover:border-button-outline-hover-border hover:shadow-none hover:bg-button-outline-hover-bg \
        active:bg-button-outline-active-bg \
        disabled:bg-button-primary-disabled-bg',
    ghost:
      'bg-button-ghost-bg text-button-ghost-text \
        hover:bg-button-ghost-hover-bg \
        active:bg-button-ghost-active-bg \
        disabled:bg-button-primary-disabled-bg',
    link: 'text-link-text rounded-none \
          hover:text-link-hover-text \
          active:text-link-active-text \
          disabled:text-link-disabled-text',
    error:
      'bg-button-error-bg text-button-error-text \
        hover:bg-button-error-hover-bg \
        active:bg-button-error-active-bg \
        disabled:bg-button-error-disabled-bg',
  },
}

const iconButtonVariants = cva(`${commonButtonStyles.base} rounded-full`, {
  variants: {
    variant: {
      default: commonButtonStyles.variants.default,
      secondary: commonButtonStyles.variants.secondary,
      outline: commonButtonStyles.variants.outline,
      ghost: commonButtonStyles.variants.ghost,
      link: commonButtonStyles.variants.link,
      error: commonButtonStyles.variants.error,
    },
    size: {
      lg: 'p-3',
      md: 'p-2.5',
      sm: 'p-1.5',
    },
  },
  defaultVariants: {
    size: 'md',
    variant: 'default',
  },
})

const buttonVariants = cva(`${commonButtonStyles.base}`, {
  variants: {
    variant: {
      default: commonButtonStyles.variants.default,
      secondary: commonButtonStyles.variants.secondary,
      outline: commonButtonStyles.variants.outline,
      ghost: commonButtonStyles.variants.ghost,
      link: commonButtonStyles.variants.link,
      error: commonButtonStyles.variants.error,
    },
    size: {
      lg: 'px-6 py-3',
      md: 'px-5 py-2 text-sm',
      sm: 'px-2.5 py-1 text-xs',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
})

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  customType?: 'button' | 'icon'
  as?: string
  isLoading?: boolean
  hideLoadingText?: boolean
  isDisabled?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      children,
      size,
      isLoading = false,
      hideLoadingText = false,
      isDisabled = false,
      as = 'button',
      customType = 'button',
      ...props
    }: ButtonProps,
    ref,
  ) => {
    const Comp = as === 'slot' ? Slot : as
    const disabled = isLoading || isDisabled
    const isIcon = customType === 'icon'
    return (
      <Comp
        className={
          isIcon
            ? cn(iconButtonVariants({ variant, size, className }))
            : cn(
                buttonVariants({
                  variant,
                  size,
                  className,
                }),
              )
        }
        ref={ref}
        disabled={disabled}
        aria-busy={isLoading}
        tabIndex={disabled ? -1 : 0}
        {...props}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {hideLoadingText ? null : <span>Loading...</span>}
            <span className="sr-only">
              Please wait while the action is processing
            </span>
          </>
        ) : (
          children
        )}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants, commonButtonStyles }
